import type { ReactNode } from 'react'
import type { JSONContent } from '@tiptap/core'
import { AlertTriangle, GitCompareArrows } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  TiptapInlineDiffView,
  TiptapInlineLoadingView,
} from './TiptapInlineDiffView'
import {
  TiptapSideBySideDiffView,
  TiptapSideBySideLoadingView,
} from './TiptapSideBySideDiffView'

export type TiptapDiffViewMode = 'side-by-side' | 'inline'

type TiptapDiffViewProps = {
  previous: JSONContent | null
  current: JSONContent | null
  previousLabel: string
  currentLabel: string
  layout?: 'previous-first' | 'current-first'
  viewMode?: TiptapDiffViewMode
  controls?: ReactNode
  loading?: boolean
  error?: string | null
  className?: string
}

export function TiptapDiffView({
  previous,
  current,
  previousLabel,
  currentLabel,
  layout = 'previous-first',
  viewMode = 'side-by-side',
  controls,
  loading = false,
  error = null,
  className,
}: TiptapDiffViewProps) {
  if (loading) {
    return (
      <DiffShell
        previousLabel={previousLabel}
        currentLabel={currentLabel}
        layout={layout}
        controls={controls}
        className={className}
      >
        {viewMode === 'inline' ? (
          <TiptapInlineLoadingView />
        ) : (
          <TiptapSideBySideLoadingView
            previousLabel={previousLabel}
            currentLabel={currentLabel}
            layout={layout}
          />
        )}
      </DiffShell>
    )
  }

  if (error) {
    return (
      <DiffShell
        previousLabel={previousLabel}
        currentLabel={currentLabel}
        layout={layout}
        controls={controls}
        className={className}
      >
        <div className='flex min-h-0 flex-1 items-center justify-center px-6 py-5'>
          <div className='w-full max-w-[720px] rounded-sm border border-status-orange/30 bg-status-orange-bg px-6 py-5 text-status-orange-strong'>
            <div className='flex items-start gap-3'>
              <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-status-orange' />
              <div>
                <p className='text-sm font-semibold'>
                  Failed to load comparison
                </p>
                <p className='mt-1 text-sm leading-6 text-status-orange-strong'>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </DiffShell>
    )
  }

  if (!current) {
    return (
      <div
        className={cn(
          'border-border-divider flex min-h-[220px] w-full max-w-[960px] items-center justify-center rounded-sm border border-dashed bg-card px-6 text-center shadow-[0_2px_12px_rgba(0,0,0,0.06)]',
          className
        )}
      >
        <div>
          <GitCompareArrows className='text-text-placeholder mx-auto h-5 w-5' />
          <p className='text-text-secondary mt-2 text-[13px] font-medium'>
            Select two versions to compare
          </p>
          <p className='text-text-subtle mt-1 text-[12px]'>
            Both version snapshots must be loaded before the diff can render.
          </p>
        </div>
      </div>
    )
  }

  const headerTitle = !previous
    ? `Select a version to compare with ${currentLabel}`
    : undefined
  const reverseComparison = layout === 'current-first' && previous !== null

  return (
    <DiffShell
      previousLabel={previousLabel}
      currentLabel={currentLabel}
      layout={layout}
      controls={controls}
      title={headerTitle}
      className={className}
    >
      {viewMode === 'inline' ? (
        <TiptapInlineDiffView
          previous={reverseComparison ? current : previous}
          current={reverseComparison ? previous : current}
          previousLabel={reverseComparison ? currentLabel : previousLabel}
          currentLabel={reverseComparison ? previousLabel : currentLabel}
        />
      ) : (
        <TiptapSideBySideDiffView
          previous={previous}
          current={current}
          previousLabel={previousLabel}
          currentLabel={currentLabel}
          layout={layout}
        />
      )}
    </DiffShell>
  )
}

function DiffShell({
  previousLabel,
  currentLabel,
  layout,
  controls,
  title,
  className,
  children,
}: {
  previousLabel: string
  currentLabel: string
  layout: 'previous-first' | 'current-first'
  controls?: ReactNode
  title?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex h-full min-h-0 w-full max-w-[1180px] flex-col rounded-sm bg-card shadow-[0_2px_12px_rgba(0,0,0,0.06)]',
        className
      )}
    >
      <DiffHeader
        previousLabel={previousLabel}
        currentLabel={currentLabel}
        layout={layout}
        controls={controls}
        title={title}
      />
      {children}
    </div>
  )
}

function DiffHeader({
  previousLabel,
  currentLabel,
  layout,
  controls,
  title,
}: {
  previousLabel: string
  currentLabel: string
  layout: 'previous-first' | 'current-first'
  controls?: ReactNode
  title?: string
}) {
  const leftLabel = layout === 'current-first' ? currentLabel : previousLabel
  const rightLabel = layout === 'current-first' ? previousLabel : currentLabel

  return (
    <div className='border-border shrink-0 border-b px-5 py-3'>
      <div className='flex min-w-0 items-center gap-3'>
        <div className='text-text-secondary flex shrink-0 items-center gap-2 text-[12px] font-medium'>
          <GitCompareArrows className='h-4 w-4 text-cobalt-strong' />
          {title ?? `Comparing ${leftLabel} -> ${rightLabel}`}
        </div>
        {controls && <div className='min-w-0 flex-1'>{controls}</div>}
      </div>
    </div>
  )
}
